import * as React from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { ActivityType, ChartScanActivity } from '@/lib/api/company/chartScan'
import { getActivityIcon } from '@/modules/drivers/helpers/ActivityIconResolver'
import { parseChartIso } from '@/modules/imports/components/TachographChartCanvas'
import { activityLabel, useChartT } from '../i18n'

// 5 totals (Driving, Work, Availability, Rest, NoData) computed from activities.
// 1:1 with WPF TachographChart.RefreshSummary (TachographChart.cs:736-752):
//   Activities.Where(x => x.Type == … && !x.IsOutOfDay).Sum(…)
// i.e. WPF *excludes* activities flagged IsOutOfDay (those between dayEnd and
// dayStart, going forward in time around midnight). The summary therefore
// reports only the time inside the day window.
type Props = {
  activities: ChartScanActivity[]
  dayStartDate?: string
  dayEndDate?: string
}

const ROWS: { type: number; color: string }[] = [
  { type: ActivityType.Driving, color: 'rgb(218, 37, 29)' },
  { type: ActivityType.Work, color: 'rgb(231, 120, 23)' },
  { type: ActivityType.Availability, color: 'rgb(255, 218, 0)' },
  { type: ActivityType.Rest, color: 'rgb(117, 197, 240)' },
  { type: ActivityType.NoData, color: '#94a3b8' },
]

const DAY_MS = 24 * 60 * 60 * 1000

export function ChartSummary({ activities, dayStartDate, dayEndDate }: Props) {
  const T = useChartT()
  const totals = React.useMemo(() => {
    const map = new Map<number, number>()
    // dayStart / dayEnd as ms-since-midnight (= minute-of-day, in ms).
    // We work in absolute calendar ms internally, but the WPF `IsOutOfDay`
    // semantics are about TIME-OF-DAY membership in the [dayStart, dayEnd]
    // window (which can wrap midnight). Compute the in-day portion of each
    // activity by treating the activity as a shifted [start, end) interval
    // on the abstract 24-h ring and intersecting with the day window.
    const dayStartMin = dayStartDate != null ? minuteOfDay(dayStartDate) : 0
    const dayEndMin = dayEndDate != null ? minuteOfDay(dayEndDate) : 24 * 60
    for (const a of activities) {
      const startMin = minuteOfDay(a.startTime)
      const endMinRaw = minuteOfDay(a.endTime)
      // Wrap-aware end: if endMin <= startMin treat as +24h on the ring so
      // we have a proper [start, end) interval with end > start.
      const endMin = endMinRaw <= startMin ? endMinRaw + 24 * 60 : endMinRaw
      const overlapMin = inDayOverlap(startMin, endMin, dayStartMin, dayEndMin)
      if (overlapMin > 0) {
        map.set(a.type, (map.get(a.type) ?? 0) + overlapMin * 60_000)
      }
    }
    return map
  }, [activities, dayStartDate, dayEndDate])

  return (
    <div className="tw-rounded tw-border tw-border-slate-200 tw-bg-white">
      <div className="tw-border-b tw-border-slate-200 tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-uppercase tw-text-slate-500">
        {T('summary', 'Podsumowanie')}
      </div>
      <ul>
        {ROWS.map((row) => (
          <li
            key={row.type}
            className="tw-flex tw-items-center tw-justify-between tw-border-b tw-border-slate-100 tw-px-3 tw-py-1.5 last:tw-border-b-0"
          >
            <div className="tw-flex tw-items-center tw-gap-2">
              <FontAwesomeIcon
                icon={getActivityIcon(row.type) as IconProp}
                style={{ color: row.color }}
              />
              <span className="tw-text-sm tw-text-slate-700">
                {activityLabel(T, row.type)}
              </span>
            </div>
            <span className="tw-font-mono tw-text-sm tw-text-slate-900">
              {formatHM(totals.get(row.type) ?? 0)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

function minuteOfDay(iso: string): number {
  const d = parseChartIso(iso)
  return d.getUTCHours() * 60 + d.getUTCMinutes()
}

// Return the number of minutes of [start, end) that fall inside the day
// window [dayStart, dayEnd]. Both intervals live on the 24-h ring; the day
// window can wrap midnight (dayEnd < dayStart). The activity interval has
// start in [0, 1440) and end in [start+1, start+1440].
function inDayOverlap(
  start: number,
  end: number,
  dayStart: number,
  dayEnd: number,
): number {
  // Decompose the day window into one or two contiguous [a, b) ranges in
  // [0, 1440). For wrap (dayEnd < dayStart): two ranges, [dayStart, 1440) and
  // [0, dayEnd]. For non-wrap: one range [dayStart, dayEnd].
  const windows: [number, number][] = dayStart <= dayEnd
    ? [[dayStart, dayEnd]]
    : [[dayStart, 24 * 60], [0, dayEnd]]
  let total = 0
  for (const [ws, we] of windows) {
    // Intersect [start, end) with [ws, we) on the ring. The activity may
    // straddle midnight (end > 1440); treat it as two halves: [start, 1440)
    // and [0, end - 1440), each intersected with the window.
    if (end <= 24 * 60) {
      total += Math.max(0, Math.min(end, we) - Math.max(start, ws))
    } else {
      total += Math.max(0, Math.min(24 * 60, we) - Math.max(start, ws))
      total += Math.max(0, Math.min(end - 24 * 60, we) - Math.max(0, ws))
    }
  }
  return total
}

function formatHM(ms: number): string {
  const totalMin = Math.round(ms / 60000)
  const h = Math.floor(totalMin / 60)
  const m = totalMin - h * 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}
